import { useApolloClient } from "@apollo/client";
import { useIsFocused } from "@react-navigation/native";
import {
    Alert,
    Button,
    Card,
    CheckBox,
    DateTimePickerInput,
    Headline,
    IconButton,
    InputControl,
    InputLabel,
    Loading,
    Spacer,
    StyleFunction,
    Text,
    useMe,
    useTheme,
    useThemedStyle,
} from "@venuepos/react-common";
import { add, set, sub } from "date-fns";
import {
    DashboardDocument,
    GQDashboard,
    GQDashboardQuery,
    useDepartmentsQuery,
    useSubscribeToDashboardSubscription,
    useUserRoleConfigurationQuery,
    useUserRoleConfigurationSaveMutation,
} from "graphql-sdk";
import produce from "immer";
import { camelToSnake, dateToDateTime, formatDate, formatDateTime } from "lib";
import { AvailableLocale, useTranslation } from "locales";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Responsive, WidthProvider } from "react-grid-layout";
import { View, ViewStyle } from "react-native";
import { Divider, Menu } from "react-native-paper";
import Select, { MultiValue } from "react-select";
import { withSize } from "react-sizeme";
import { selectComponents, selectStyles } from "../../components/multi-picker";
import { useHandleMutationError } from "../../hooks";
import { useAdminSession } from "../../session";
import { AdminContainer } from "../container";
import { dashboardStyleFunc } from "./styles";
import { Bookkeeping } from "./widgets/bookkeeping";
import { DepartmentSalesTop10 } from "./widgets/department-sales-top10";
import { InvoiceAmountPerHour } from "./widgets/invoice-amount-per-hour";
import { InvoiceAverageTotal } from "./widgets/invoice-average-total";
import { InvoiceTotal } from "./widgets/invoice-total";
import { ProductGroupTop10 } from "./widgets/product-group-top10";
import { ProductSalesTop10 } from "./widgets/product-sales-top10";
import { TurnoverTotal } from "./widgets/turnover-total";

export default withSize({ refreshMode: "debounce", refreshRate: 60 })(
    MerchantDashboardScreen
);

export type CompareData = {
    date: string;
    dashboard: GQDashboard;
};
// To fix problem with @types/react-grid-layout
const ResponsiveGridLayout = WidthProvider(Responsive) as any;

// t("dashboard.names.invoice_total","Number of transactions")
// t("dashboard.names.turnover_total","Turnover")
// t("dashboard.names.invoice_average_total","Avg. transaction")
// t("dashboard.names.product_sales_top10","Product Sales - Top 10")
// t("dashboard.names.department_sales_top10","Department Sales - Top 10")
// t("dashboard.names.product_group_top10","Product Groups - Top 10")
// t("dashboard.names.bookkeeping","Bookkeeping")
// t("dashboard.names.invoice_amount_per_hour","Invoice amount per hour")
const allWidgets = [
    {
        id: "InvoiceTotal",
        component: (data: CompareData, compareTo?: CompareData) => (
            <InvoiceTotal data={data} compareTo={compareTo} />
        ),
    },
    {
        id: "TurnoverTotal",
        component: (
            data: CompareData,
            compareTo?: CompareData,
            locale?: AvailableLocale
        ) => (
            <TurnoverTotal data={data} compareTo={compareTo} locale={locale} />
        ),
    },
    {
        id: "InvoiceAverageTotal",
        component: (
            data: CompareData,
            compareTo?: CompareData,
            locale?: AvailableLocale
        ) => (
            <InvoiceAverageTotal
                data={data}
                compareTo={compareTo}
                locale={locale}
            />
        ),
    },
    {
        id: "ProductSalesTop10",
        component: (
            data: CompareData,
            _compareTo?: CompareData,
            locale?: AvailableLocale
        ) => <ProductSalesTop10 data={data} locale={locale} />,
    },
    {
        id: "DepartmentSalesTop10",
        component: (
            data: CompareData,
            _compareTo?: CompareData,
            locale?: AvailableLocale
        ) => <DepartmentSalesTop10 data={data} locale={locale} />,
    },
    {
        id: "ProductGroupTop10",
        component: (
            data: CompareData,
            _compareTo?: CompareData,
            locale?: AvailableLocale
        ) => <ProductGroupTop10 data={data} locale={locale} />,
    },
    {
        id: "Bookkeeping",
        component: (
            data: CompareData,
            _compareTo?: CompareData,
            locale?: AvailableLocale
        ) => <Bookkeeping data={data} locale={locale} />,
    },
    {
        id: "InvoiceAmountPerHour",
        component: (
            data: CompareData,
            compareTo?: CompareData,
            locale?: AvailableLocale
        ) => (
            <InvoiceAmountPerHour
                data={data}
                compareTo={compareTo}
                locale={locale}
            />
        ),
    },
];

// Initial layout of the dashboard
const initialLayouts: ReactGridLayout.Layouts = {
    lg: [
        { i: "InvoiceTotal", x: 0, y: 0, w: 4, h: 7, minW: 2, minH: 7 },
        { i: "TurnoverTotal", x: 4, y: 0, w: 4, h: 7, minW: 2, minH: 7 },
        { i: "InvoiceAverageTotal", x: 8, y: 0, w: 4, h: 7, minW: 2, minH: 7 },
        { i: "ProductSalesTop10", x: 0, y: 3, w: 6, h: 13, minW: 2, minH: 7 },
        {
            i: "DepartmentSalesTop10",
            x: 6,
            y: 3,
            w: 6,
            h: 13,
            minW: 2,
            minH: 7,
        },
        { i: "ProductGroupTop10", x: 0, y: 8, w: 6, h: 13, minW: 2, minH: 7 },
        { i: "Bookkeeping", x: 6, y: 8, w: 6, h: 13, minW: 2, minH: 7 },
        {
            i: "InvoiceAmountPerHour",
            x: 0,
            y: 8,
            w: 12,
            h: 13,
            minW: 6,
            minH: 13,
        },
    ],
    md: [
        { i: "InvoiceTotal", x: 0, y: 0, w: 6, h: 7, minW: 2, minH: 7 },
        { i: "TurnoverTotal", x: 6, y: 0, w: 6, h: 7, minW: 2, minH: 7 },
        { i: "InvoiceAverageTotal", x: 12, y: 0, w: 6, h: 7, minW: 2, minH: 7 },
        { i: "ProductSalesTop10", x: 18, y: 3, w: 6, h: 13, minW: 2, minH: 7 },
        {
            i: "DepartmentSalesTop10",
            x: 6,
            y: 3,
            w: 12,
            h: 13,
            minW: 2,
            minH: 7,
        },
        { i: "ProductGroupTop10", x: 18, y: 8, w: 6, h: 13, minW: 2, minH: 7 },
        { i: "Bookkeeping", x: 18, y: 8, w: 6, h: 13, minW: 2, minH: 7 },
        {
            i: "InvoiceAmountPerHour",
            x: 0,
            y: 12,
            w: 12,
            h: 13,
            minW: 4,
            minH: 13,
        },
    ],
};

const gridPadding: [number, number] = [0, 20];
const gridMargin: [number, number] = [20, 20];
const gridBreakpoints = { lg: 1200, md: 996 };
const gridCols = { lg: 12, md: 6 };

export function MerchantDashboardScreen() {
    const graphQlClient = useApolloClient();
    const [t] = useTranslation();
    const theme = useTheme();
    const styles = useThemedStyle(styleFunc);
    const sharedStyles = useThemedStyle(dashboardStyleFunc);
    const me = useMe();
    const { handleMutationError } = useHandleMutationError();

    const [dashboardData, setDashboardData] = useState<GQDashboard>();
    const [fromDate, setFromDate] = useState<Date>(today());

    const [useCompareDate, setUseCompareDate] = useState<boolean>(false);
    const [compareData, setCompareData] = useState<GQDashboard>();
    const [compareDate, setCompareDate] = useState<Date>(previousDay(fromDate));

    const [isLoading, setIsLoading] = useState(false);
    const [{ locale }] = useAdminSession(["locale"]);

    // Contains the widgets shown on the dashboard
    const [widgets, setWidgets] = useState(allWidgets);
    // Contains the layout of the dashboard
    const [layouts, setLayouts] =
        useState<ReactGridLayout.Layouts>(initialLayouts);
    // Used for storing the widgets from before editing. If the user cancels editing this will be used as widgets again
    const [originalWidgets, setOriginalWidgets] = useState(allWidgets);
    // Used for storing the layout from before editing. If the user cancels editing this will be used as layout again
    const [originalLayouts, setOriginalLayouts] =
        useState<ReactGridLayout.Layouts>();
    const [isEditing, setIsEditing] = useState(false);
    const [width, setWidth] = useState(0);

    const [widgetMenuVisible, setWidgetMenuVisible] = useState(false);
    const [departmentIds, setDepartmentIds] = useState<string[]>([]);
    const [configurationLoaded, setConfigurationLoaded] = useState(false);
    const isFocused = useIsFocused();

    // Used for checking if we are showing the dashboard for the current day
    // (and thus should support live updates) or if the user has browsed to
    // another day for historical data.
    //
    // The current day is determined by the "startOfDay", an offset from 0:00.
    // This means that the "current day" could be from yesterday at 06:00 to today 06:00
    // even though the actual date differs from yesterday.
    const now = useMemo(() => new Date(), []);
    const toDate = useMemo(() => nextDay(fromDate), [fromDate]);
    const afterStart = useMemo(() => now > fromDate, [fromDate, now]);
    const beforeEnd = useMemo(() => now < toDate, [now, toDate]);
    const inTodayPeriod = useMemo(
        () => afterStart && beforeEnd,
        [afterStart, beforeEnd]
    );

    const compareToDate = useMemo(() => nextDay(compareDate), [compareDate]);

    const widgetData = useMemo(
        () =>
            dashboardData
                ? {
                      date: formatDate(fromDate, locale as AvailableLocale),
                      dashboard: dashboardData,
                  }
                : null,
        [dashboardData, fromDate, locale]
    );

    const widgetCompareData = useMemo(
        () =>
            useCompareDate &&
            fromDate.toISOString() !== compareDate.toISOString() &&
            compareData
                ? {
                      date: formatDate(compareDate, locale as AvailableLocale),
                      dashboard: compareData,
                  }
                : null,
        [useCompareDate, fromDate, compareDate, compareData, locale]
    );

    const { data: liveData, error: liveError } =
        useSubscribeToDashboardSubscription({
            variables: {
                from: fromDate,
                to: toDate,
                departmentIds,
            },

            // The isFocused is used
            shouldResubscribe: isFocused && inTodayPeriod,
            // Skip subscription if query is loading. Apollo becomes cranky if we run this at the same time
            skip: !isFocused || !inTodayPeriod,
        });

    const { data: departmentsData } = useDepartmentsQuery({
        variables: {
            pagination: {
                page: 0,
                pageSize: 999999,
                sort: "name",
                sortDirection: "ASC",
            },
        },
        fetchPolicy: "network-only",
    });

    const { data: userRoleData } = useUserRoleConfigurationQuery({
        variables: {
            userId: me.user!.id,
            merchantId: me.merchant!.id,
            roleId: me.role!.id,
            type: "merchantDashboard",
        },
    });
    const [userRoleConfigurationSave] = useUserRoleConfigurationSaveMutation();

    // When configuration has been loaded, update the widgets, layout and departments
    useEffect(() => {
        if (
            !configurationLoaded &&
            userRoleData &&
            userRoleData.userRoleConfiguration
        ) {
            const json = JSON.parse(userRoleData.userRoleConfiguration.data);
            setLayouts(json.layout);
            setDepartmentIds(json.departmentIds);
            const loadedWidgets: {
                id: string;
                component: (
                    data: CompareData,
                    compareTo?: CompareData
                ) => JSX.Element;
            }[] = [];

            for (let i = 0; i < json.layout.lg.length; i++) {
                const widget = allWidgets.find(
                    itr => itr.id === json.layout.lg[i].i
                );
                if (widget) {
                    loadedWidgets.push(widget);
                }
            }
            if (loadedWidgets && loadedWidgets.length > 0) {
                setWidgets(loadedWidgets);
            }
            setConfigurationLoaded(true);
        }
    }, [configurationLoaded, userRoleData]);

    useEffect(() => {
        // This is triggered, when the subscription/websocket receives a new message.
        // There is no date checking here, since the subscription is only active on the "current day"

        if (liveData?.dashboardUpdated) {
            setDashboardData(liveData?.dashboardUpdated as GQDashboard);
        }
    }, [liveData?.dashboardUpdated]);

    const fetchDashboardData = useCallback(
        (
            from: Date,
            to: Date,
            depIds: string[],
            storeAsCompareData?: boolean
        ) => {
            setIsLoading(true);
            graphQlClient
                .query<GQDashboardQuery>({
                    query: DashboardDocument,
                    fetchPolicy: storeAsCompareData
                        ? "cache-first"
                        : "no-cache",
                    variables: {
                        from,
                        to,
                        departmentIds: depIds,
                    },
                })
                .then(result => {
                    setIsLoading(false);
                    if (result.data.dashboard) {
                        if (storeAsCompareData) {
                            setCompareData(result.data.dashboard);
                        } else {
                            setDashboardData(result.data.dashboard);
                        }
                    } else {
                        console.warn(
                            "There is no useful data in the response from GQL query Dashboard()."
                        );
                    }
                });
        },
        [graphQlClient]
    );

    // The initial call to fetch dashboard data. Happens only on the first load.
    useEffect(() => {
        fetchDashboardData(fromDate, toDate, departmentIds);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleDateChange = useCallback(
        (dateObj: Date) => {
            if (!dateObj) {
                return;
            }

            const from = today(dateObj);
            const to = nextDay(dateObj);

            setFromDate(from);

            fetchDashboardData(from, to, departmentIds, false);
        },
        [departmentIds, fetchDashboardData]
    );

    const handleCompareDateChange = useCallback(
        (dateObj: Date) => {
            if (!dateObj) {
                return;
            }

            const from = today(dateObj);

            setCompareDate(from);

            if (from.toISOString() === fromDate.toISOString()) {
                return;
            }

            fetchDashboardData(from, nextDay(from), departmentIds, true);
        },
        [departmentIds, fetchDashboardData, fromDate]
    );

    const handleEditing = useCallback(() => {
        setOriginalWidgets(widgets);
        setOriginalLayouts(layouts);
        setIsEditing(!isEditing);
    }, [isEditing, layouts, widgets]);

    const handleDepartmentChange = useCallback(
        (
            newValue: MultiValue<{
                value: string;
                label: string;
            }>
        ) => {
            setDepartmentIds(newValue.map(itr => itr.value));
        },
        []
    );

    const handleLayoutChange = (
        _currentLayout: ReactGridLayout.Layout[],
        allLayouts: ReactGridLayout.Layouts
    ) => {
        setLayouts(allLayouts);
    };

    const handleLayoutSave = useCallback(async () => {
        setIsEditing(false);
        await handleMutationError(
            async () =>
                await userRoleConfigurationSave({
                    variables: {
                        userId: me.user!.id,
                        merchantId: me.merchant!.id,
                        roleId: me.role!.id,
                        type: "merchantDashboard",
                        configuration: JSON.stringify({
                            departmentIds: departmentIds,
                            layout: layouts,
                        }),
                    },
                }),
            t("common.saved", "Saved")
        );
    }, [
        departmentIds,
        handleMutationError,
        layouts,
        me.merchant,
        me.role,
        me.user,
        t,
        userRoleConfigurationSave,
    ]);

    const handleLayoutCancel = () => {
        setIsEditing(false);
        setWidgets(originalWidgets);
        if (originalLayouts) {
            setLayouts(originalLayouts);
        }
    };

    const handleRemoveWidget = (itemId: string) => {
        setWidgets(widgets.filter(i => i.id !== itemId));
    };

    const handleAddWidget = useCallback(
        (itemId: string) => {
            setWidgets([
                ...widgets,
                allWidgets.find(itr => itr.id === itemId)!,
            ]);
            const addLayoutLg = initialLayouts.lg.find(itr => itr.i === itemId);
            const addLayoutMd = initialLayouts.md.find(itr => itr.i === itemId);
            setLayouts(
                produce(layouts, draft => {
                    draft.lg.push(addLayoutLg!);
                    draft.md.push(addLayoutMd!);
                })
            );
        },
        [layouts, widgets]
    );

    const handleUseCompareDate = useCallback(
        newValue => {
            if (!compareData) {
                handleCompareDateChange(compareDate);
            }

            setUseCompareDate(newValue);
        },
        [compareData, compareDate, handleCompareDateChange]
    );

    if (liveError) {
        return <Alert type="error">{`Error: ${liveError.message}`}</Alert>;
    }

    if (!dashboardData) {
        return (
            <AdminContainer>
                <Loading message={t("common.loading", "Loading")} />
            </AdminContainer>
        );
    }

    return (
        <AdminContainer>
            <View
                style={sharedStyles.topRow}
                testID="merchantDashboard"
                onLayout={event => {
                    setWidth(event.nativeEvent.layout.width);
                }}
            >
                <View style={theme.styles.flex1}>
                    <View style={[theme.styles.row, theme.styles.defaultGap]}>
                        <DateTimePickerInput
                            dateTimeType="date"
                            label={t("common.date", "Date")}
                            onChangeDate={handleDateChange}
                            value={dateToDateTime(fromDate)}
                            maxDate={today()}
                            showNavigationArrows={true}
                        />
                    </View>
                    <View style={theme.styles.row}>
                        <Text style={styles.dateText}>
                            {formatDateTime(
                                fromDate,
                                locale as AvailableLocale,
                                "Pp" // "Pp" = dd/MM/yyyy hh:mm, i.e. no seconds
                            )}
                        </Text>
                        <Text style={styles.dateText}> - </Text>
                        <Text style={styles.dateText}>
                            {formatDateTime(
                                toDate,
                                locale as AvailableLocale,
                                "Pp" // "Pp" = dd/MM/yyyy hh:mm, i.e. no seconds
                            )}
                        </Text>
                    </View>
                </View>
                <View style={theme.styles.flex1}>
                    <View style={[theme.styles.row, theme.styles.defaultGap]}>
                        <CheckBox
                            value={useCompareDate}
                            onValueChange={handleUseCompareDate}
                            style={styles.checkbox}
                        />
                        <View>
                            <InputLabel>
                                {t("dashboard.compare_to", "Compare to")}
                            </InputLabel>
                            <DateTimePickerInput
                                dateTimeType="date"
                                onChangeDate={handleCompareDateChange}
                                value={dateToDateTime(compareDate)}
                                maxDate={today()}
                                showNavigationArrows={true}
                            />
                            {useCompareDate ? (
                                <View style={theme.styles.row}>
                                    <Text style={styles.dateText}>
                                        {formatDateTime(
                                            compareDate,
                                            locale as AvailableLocale,
                                            "Pp" // "Pp" = dd/MM/yyyy hh:mm, i.e. no seconds
                                        )}
                                    </Text>
                                    <Text style={styles.dateText}> - </Text>
                                    <Text style={styles.dateText}>
                                        {formatDateTime(
                                            compareToDate,
                                            locale as AvailableLocale,
                                            "Pp" // "Pp" = dd/MM/yyyy hh:mm, i.e. no seconds
                                        )}
                                    </Text>
                                </View>
                            ) : null}
                        </View>
                    </View>
                </View>
                <Button onPress={handleEditing} disabled={isEditing}>
                    {t("common.edit", "Edit")}
                </Button>
            </View>

            {isEditing && (
                <Card style={styles.topBar}>
                    <Card.Content>
                        <View style={theme.styles.row}>
                            <InputControl
                                description={t(
                                    "dashboard.widgets.department_description",
                                    "Select department(s) to show. If no department is selected all departments is shown"
                                )}
                            >
                                <InputLabel>
                                    {t("common.department", "Department")}
                                </InputLabel>
                                <Select
                                    isMulti={true}
                                    hideSelectedOptions={false}
                                    options={
                                        // Map the departments to the format for the select
                                        departmentsData?.departments.data.map(
                                            item => {
                                                return {
                                                    value: item.id,
                                                    label: item.name,
                                                };
                                            }
                                        )
                                    }
                                    defaultValue={
                                        // Filter for only the selected departments from configuration
                                        departmentsData?.departments.data
                                            .filter(itr =>
                                                departmentIds.includes(itr.id)
                                            )
                                            // Map the departments to the format for the select
                                            .map(item => {
                                                return {
                                                    value: item.id,
                                                    label: item.name,
                                                };
                                            }) || []
                                    }
                                    onChange={handleDepartmentChange}
                                    styles={selectStyles}
                                    components={selectComponents}
                                />
                            </InputControl>
                            <Spacer space={2} />
                            <Menu
                                visible={widgetMenuVisible}
                                onDismiss={() => setWidgetMenuVisible(false)}
                                anchor={
                                    <>
                                        <Spacer space={2} />
                                        <Button
                                            style={styles.button}
                                            onPress={() =>
                                                setWidgetMenuVisible(true)
                                            }
                                        >
                                            {t(
                                                "dashboard.widgets.header",
                                                "Widgets"
                                            )}
                                        </Button>
                                    </>
                                }
                            >
                                <View style={styles.widgetOptionContainer}>
                                    <Spacer />
                                    <Headline
                                        size="h5"
                                        style={styles.widgetHeader}
                                    >
                                        {t(
                                            "dashboard.widgets.header",
                                            "Widgets"
                                        )}
                                    </Headline>

                                    <Divider style={styles.widgetDivider} />
                                    <Spacer />
                                    {allWidgets.map(({ id }, index) => {
                                        return (
                                            <CheckBox
                                                key={`checkbox:${id}`}
                                                style={[
                                                    index % 2
                                                        ? theme.styles.oddRow
                                                        : undefined,
                                                ]}
                                                label={t(
                                                    "dashboard.names." +
                                                        camelToSnake(id),
                                                    id
                                                )}
                                                value={
                                                    widgets.find(
                                                        itr => itr.id === id
                                                    ) !== undefined
                                                }
                                                onValueChange={value => {
                                                    if (value) {
                                                        handleAddWidget(id);
                                                    } else {
                                                        handleRemoveWidget(id);
                                                    }
                                                }}
                                            />
                                        );
                                    })}
                                </View>
                            </Menu>
                        </View>
                    </Card.Content>

                    <Card.Actions>
                        <Button
                            style={styles.button}
                            onPress={handleLayoutSave}
                        >
                            {t("common.save", "Save")}
                        </Button>
                    </Card.Actions>
                    <IconButton
                        name="close"
                        style={styles.closeIcon}
                        onPress={handleLayoutCancel}
                    />
                </Card>
            )}
            {isLoading ? (
                <>
                    <Spacer />
                    <Loading />
                </>
            ) : (
                <ResponsiveGridLayout
                    className="layout"
                    layouts={layouts}
                    rowHeight={10}
                    width={width}
                    autoSize={true}
                    onLayoutChange={handleLayoutChange}
                    margin={gridMargin}
                    isDraggable={isEditing}
                    isResizable={isEditing}
                    isDroppable={isEditing}
                    breakpoints={gridBreakpoints}
                    cols={gridCols}
                    containerPadding={gridPadding}
                >
                    {widgets.map(({ id, component }) => {
                        return (
                            <div key={id} className="widget">
                                <Card style={sharedStyles.widgetContent}>
                                    {component(
                                        widgetData,
                                        widgetCompareData,
                                        locale as AvailableLocale
                                    )}
                                </Card>
                                {isEditing && (
                                    <div className="gridHandle">
                                        <IconButton
                                            onPress={() => {
                                                handleRemoveWidget(id);
                                            }}
                                            name="close"
                                            style={styles.closeIcon}
                                        />
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </ResponsiveGridLayout>
            )}
        </AdminContainer>
    );
}

const startOfDay = {
    hours: 6,
    minutes: 0,
    seconds: 0,
    milliseconds: 0,
};

const today = (date = new Date()) => {
    const returnDate = set(date, startOfDay);

    // If the current time is before we consider the current day started
    // (e.g. at 4.30 in the morning, but we count the current day from 06:00 to 06:00),
    // then use the previous day
    if (date < returnDate) {
        return previousDay(date);
    }

    return returnDate;
};
const previousDay = (date = new Date()) =>
    set(sub(date, { days: 1 }), startOfDay);
const nextDay = (date = new Date()) => set(add(date, { days: 1 }), startOfDay);

const styleFunc: StyleFunction = (theme, dimensions) => ({
    closeIcon: {
        position: "absolute",
        color: theme.colors.primary,
        top: 0,
        right: 0,
        elevation: 1,
    },
    button: {
        minWidth: 150,
    },
    widgetOptionContainer: {
        marginHorizontal: theme.spacingScale * 2,
    },
    widgetHeader: {
        ...(theme.headlines.h5 as ViewStyle),
        marginBottom: theme.spacingScale,
    },
    widgetDivider: {
        marginHorizontal: -(theme.spacingScale * 2),
        backgroundColor: theme.colors.grey100,
    },
    topBar: {
        marginTop: theme.spacingScale * 2,
        zIndex: 100,
    },

    dateText: {
        ...theme.styles.text,
        ...theme.styles.smallText,
        fontWeight: "bold",
    },

    dateColumn: {
        ...theme.styles.flex1,
        flexDirection: dimensions.width > 1440 ? "row" : "column",
    },

    checkbox: {
        paddingTop: theme.spacingScale * 2.5,
    },
});
